import React from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import {
  ForumOutlined as ForumIcon,
  EventOutlined as EventIcon,
  HandymanOutlined as HandymanIcon,
  HomeRepairServiceOutlined as UpkeepIcon,
} from '@mui/icons-material';
import { CONTENT_MAX_WIDTH } from '../../components/layout/PageLayout';
import RegisterForm from '../../components/auth/RegisterForm';

/**
 * What the account is for, in the words of things somebody would actually do.
 *
 * This panel used to be a three-step progress list — "vos informations",
 * "confirmer votre courriel", "c'est prêt" — which described the form instead
 * of saying what it is for. The confirmation email is real, but it is the
 * form's to announce, once it has been sent and to which address: said
 * beforehand, it is one more hurdle in front of somebody still deciding.
 */
const WHAT_IT_OPENS = [
  {
    icon: <ForumIcon />,
    title: 'Écrire dans le fil',
    detail: 'Poser une question, donner un meuble, signaler un chat perdu.',
  },
  {
    icon: <EventIcon />,
    title: 'Annoncer un événement',
    detail: 'Un vide-grenier, une réunion, la fête des voisins.',
  },
  {
    icon: <HandymanIcon />,
    title: 'Demander un rendez-vous',
    detail: 'À un professionnel du quartier, sans chercher son numéro.',
  },
  {
    icon: <UpkeepIcon />,
    title: 'Suivre l’entretien de chez vous',
    detail: 'Chaudière, ramonage, VMC : la liste et les rappels.',
  },
];

function Opening({ icon, title, detail }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.75 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 38,
          height: 38,
          borderRadius: 999,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#ffffff',
          color: 'secondary.dark',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.dark' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#52603f' }}>
          {detail}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * The sign-up page.
 *
 * Two columns on a wide screen, and the form is the left one: it is what the
 * page is for. The panel beside it answers the only question somebody has
 * before filling in a form — what do I get — rather than describing the form
 * itself. On a phone the form comes first and the panel follows, because
 * scrolling past a sales pitch to reach the field you came for is the fastest
 * way to lose somebody who had already decided.
 */
function Register() {
  return (
    <Box sx={{ bgcolor: 'background.default', flex: 1 }}>
      <Container maxWidth={CONTENT_MAX_WIDTH} sx={{ py: { xs: 4, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ gap: { xs: 3, md: 4 }, alignItems: { md: 'flex-start' }, justifyContent: 'center' }}
        >
          <RegisterForm />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              p: { xs: 3, md: 4 },
              borderRadius: '22px',
              bgcolor: 'success.light',
              width: '100%',
              maxWidth: { md: 360 },
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography variant="h3" component="h2" sx={{ color: 'secondary.dark' }}>
                Ce que le compte permet
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: '#52603f' }}>
                C’est gratuit, et cela prend une minute.
              </Typography>
            </Box>

            {WHAT_IT_OPENS.map((opening) => (
              <Opening key={opening.title} {...opening} />
            ))}

            {/* The reassurance that matters most on a sign-up form: nobody is
                being made to register to look around. */}
            <Typography
              variant="body2"
              sx={{ pt: 2, borderTop: '1px solid', borderColor: '#d7e0c8', color: '#52603f' }}
            >
              Lire ne demande rien&nbsp;: les services, les commerces, le fil et l’agenda sont
              ouverts à tout le monde. Le compte sert à participer.
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default Register;
